import { ClientService, ToastMessageService } from '@fitness-force/api';
import { Store } from '@ngrx/store';
import { AsyncPipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { BadRequestError, NotFoundError } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';
import { STAFF_STATE, TENANTS_INFO } from '@fitness-force/state';
import type {
  STAFFINTERFACE,
  STATE_STAFF_LIST,
  TENANTSINTERFACE,
} from '@fitness-force/models';
import { MultiTenantListComponent } from '@fitness-force/common/multi-tenant-list';
import { Roles } from '@fitness-force/enum';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { RouteStateService } from '@fitness-force/shared-utils';

@Component({
  selector: 'transfer-prospect',
  templateUrl: './transfer-prospect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      .p-treeselect-label {
        color: var(--p-treeselect-color) !important;
      }
    `,
  ],
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    StyleClassModule,
    AvatarModule,
    ErrorMsgComponent,
    MultiTenantListComponent,
    TranslateModule,
    AssignedToComponent,
    ErrorMsgComponent,
    AsyncPipe,
  ],
})
export class TransferProspectComponent implements OnInit {
  private store = inject(Store);
  translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private fb = inject(UntypedFormBuilder);
  toastMessageService = inject(ToastMessageService);
  private routeStateService = inject(RouteStateService);

  readonly transferProspectModal = input<boolean | null>(false);
  readonly clientData$ = input(new BehaviorSubject<any>(null));
  readonly closeTransferProspectDialog = output<{ action: string } | boolean>();
  allClientData$ = new BehaviorSubject<any>({});
  clientData: any;
  currentTenant$ = new BehaviorSubject<any>({});
  selectedTenant$ = new BehaviorSubject<any>({});
  allTenants$ = new BehaviorSubject<any>([]);
  transferProspectFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  isloading: boolean = false;
  tenantId: number = 0;
  staffIds$ = new BehaviorSubject<number[]>([0]);
  claims: string[] = ['can_be_client_rep'];

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    this.transferProspectFormGroup = this.fb.group({
      newTenant: [null, [Validators.required]],
      newClientRep: [null, [Validators.required]],
    });
    const clientDataSubject = this.clientData$();
    if (clientDataSubject) {
      clientDataSubject.subscribe((client: any) => {
        this.allClientData$.next(client);
        this.clientData = client;
        this.store
          .select(TENANTS_INFO)
          .subscribe((tenant: TENANTSINTERFACE) => {
            this.tenantId = tenant.selected_tenant.id;
            this.allTenants$.next(tenant.accessed_tenant);
            this.currentTenant$.next(
              tenant.accessed_tenant.find(
                (t: any) => client.createdin_tenant_id == t.id,
              ),
            );
          });
        this.updateStaffList();
      });
    }
  }

  updateStaffList() {
    this.store.select(STAFF_STATE).subscribe((data: STATE_STAFF_LIST) => {
      if (data && data.staffList && data.staffList.length > 0) {
        const staffIds: number[] = [];
        // Get the currently selected tenant
        const selectedTenantId = this.NewTenant.value;

        if (selectedTenantId) {
          // Only filter staff who have access to the selected tenant AND have can_be_client_rep = true
          data.staffList.forEach((s: STAFFINTERFACE) => {
            if (s.tenant_access_id.includes(selectedTenantId)) {
              if (
                s.role == Roles.Admin ||
                s.role.toString() == Roles[Roles.Admin]
              ) {
                if (s.can_be_client_rep) staffIds.push(s.id);
              } else if (
                s.role == Roles.Staff ||
                s.role.toString() == Roles[Roles.Staff]
              ) {
                if (s.can_be_client_rep) staffIds.push(s.id);
              }
            }
          });
        } else {
          // If no tenant is selected yet, include staff with access to any active tenant
          var accessedTenantIds: number[] = [];
          this.allTenants$.subscribe((tenants: any) => {
            tenants.forEach((t: any) => {
              if (t.status.toLowerCase() == 'active') {
                accessedTenantIds.push(t.id);
              }
            });
          });

          data.staffList.forEach((s: STAFFINTERFACE) => {
            accessedTenantIds.forEach((t: number) => {
              if (s.tenant_access_id.includes(t)) {
                if (
                  s.role == Roles.Admin ||
                  s.role.toString() == Roles[Roles.Admin]
                ) {
                  if (s.can_be_client_rep) staffIds.push(s.id);
                } else if (
                  s.role == Roles.Staff ||
                  s.role.toString() == Roles[Roles.Staff]
                ) {
                  if (s.can_be_client_rep) staffIds.push(s.id);
                }
              }
            });
          });
        }

        this.staffIds$.next(staffIds);
      }
    });
  }

  get NewTenant(): UntypedFormControl {
    return this.transferProspectFormGroup.get(
      'newTenant',
    ) as UntypedFormControl;
  }

  get NewClientRep(): UntypedFormControl {
    return this.transferProspectFormGroup.get(
      'newClientRep',
    ) as UntypedFormControl;
  }

  selectTenant(tenant: any) {
    this.selectedTenant$.next(tenant);
    this.NewTenant.setValue(tenant.id);
    // Reset the client rep when tenant changes
    this.NewClientRep.setValue(null);
    // Update staff list when tenant changes
    this.updateStaffList();
  }

  transferProspect() {
    this.isloading = true;
    const transferProspectData = {
      client_ids: [this.clientData.id],
      transfer_to_centre_id: this.NewTenant.value,
      new_client_rep_id: this.NewClientRep.value
        ? this.NewClientRep.value.id
        : 0,
    };
    this.clientService
      .transferProspect(this.tenantId, transferProspectData)
      .subscribe({
        next: async (res: any) => {
          this.translate
            .get('TRANSFER_PROSPECT.MESSAGES.SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.isloading = false;
          this.routeStateService.setReloadListPage('prospect-list', true);
          await this.timeout(1000);
          this.closeDialogEvent(true);
        },
        error: (error) => {
          // Ensure loading state is reset on error
          this.isloading = false;

          if (error instanceof NotFoundError) {
            this.toastMessageService.showError(error);
          } else if (error instanceof BadRequestError) {
            this.toastMessageService.showError(error);
          } else {
            this.toastMessageService.showError(error);
          }
        },
      });
  }

  closeDialogEvent(updated = false) {
    this.closeTransferProspectDialog.emit(
      updated ? { action: 'updated' } : updated,
    );
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
